import React, { useState } from 'react';
import { Button, Modal, DatePicker, Select, message, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useLazyQuery, useQuery } from '@apollo/client';
import { GET_APPOINTMENT_SUMMARY_BY_DATE_RANGE, GET_INSURANCE_PAYORS } from '../graphql/queries';
import { useTranslation } from '../hooks/useTranslation';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const TENANT_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'default', label: 'default' },
  { value: 'townhealth', label: 'townhealth' },
  { value: 'china-life', label: 'china-life' },
];

interface AppointmentSummaryReportItem {
  appointmentDateTime: string;
  status: string;
  payment: string;
  extraMed: boolean;
  deliveryWaybill: string;
  timestamp: string;
  lastnameEn: string;
  firstnameEn: string;
  chineseName: string;
  gender: string;
  dob: string;
  hkid: string;
  appUserPhone: string;
  deliveryAddress: string;
  transactionId: string;
  district?: string;
  transactionTotal?: number;
  doctorName?: string;
  tenant?: string;
  payor?: string;
  // Waybill export fields
  appointmentId?: string;
  date?: string;
  englishName?: string;
  waybillNo?: string;
  address?: string;
  mobile?: string;
}

interface AppointmentReportExportProps {
  loading?: boolean;
}

const AppointmentReportExport: React.FC<AppointmentReportExportProps> = ({ loading: externalLoading }) => {
  const { t } = useTranslation();
  const [exportDateModalVisible, setExportDateModalVisible] = useState(false);
  const [selectedExportDateRange, setSelectedExportDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [selectedReportType, setSelectedReportType] = useState<string>('default');
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [selectedPayor, setSelectedPayor] = useState<string>('');

  const [getReportByDateRange, { loading: reportByDateRangeLoading }] = useLazyQuery(GET_APPOINTMENT_SUMMARY_BY_DATE_RANGE);
  const { data: payorsData } = useQuery(GET_INSURANCE_PAYORS);

  const showExportDateModal = () => {
    const today = dayjs();
    setSelectedExportDateRange([today, today]); // Default to today
    setSelectedReportType('default');
    setSelectedTenant('');
    setSelectedPayor('');
    setExportDateModalVisible(true);
  };

  const exportReportByDateRange = async (
    dateRange: [dayjs.Dayjs, dayjs.Dayjs],
    reportType: string = 'default',
    tenant: string = '',
    payor: string = '',
  ) => {
    try {
      const startDateStr = dateRange[0].format('YYYY-MM-DD');
      const endDateStr = dateRange[1].format('YYYY-MM-DD');
      const { data } = await getReportByDateRange({
        variables: { 
          startDate: startDateStr,
          endDate: endDateStr,
          reportType: reportType || 'default',
          tenant: tenant || undefined,
          payor: payor || undefined,
        }
      });

      if (!data?.getAppointmentSummaryByDateRange) {
        message.warning('No report data available for the selected date range');
        return;
      }

      const reportItems: any[] = data.getAppointmentSummaryByDateRange;

      // Handle different CSV structures based on report type
      let headers: string[];
      let csvContent: string;

      if (reportType === 'waybill-export-sf' || reportType === 'waybill-export-jd') {
        // SF/JD Waybill Export structure (same format for both)
        headers = [
          'Appointment ID',
          'Date',
          'Chinese Name',
          'English Name',
          'HKID',
          'Waybill No',
          'Address',
          'Mobile'
        ];

        csvContent = [
          headers.join(','),
          ...reportItems.map((row: any) => [
            `"${row.appointmentId || ''}"`,
            `"${row.date || ''}"`,
            `"${row.chineseName || ''}"`,
            `"${row.englishName || ''}"`,
            `"${row.hkid || ''}"`,
            `"${row.waybillNo || ''}"`,
            `"${(row.address || '').replace(/"/g, '""')}"`, // Escape quotes in address
            `"${row.mobile || ''}"`
          ].join(','))
        ].join('\n');
      } else {
        // Default report structure
        headers = [
          'Appointment Date & Time',
          'Status',
          'Payment',
          'Extra Med',
          'Delivery Waybill',
          'Timestamp',
          'Lastname (EN)',
          'Firstname (EN)',
          'Chinese Name',
          'Gender',
          'DoB',
          'HKID',
          'App User Phone',
          'Delivery Address',
          'Transaction ID',
          t('common.district'),
          t('common.transactionTotal'),
          t('appointments.doctorName'),
          'Tenant',
          'Payor',
        ];

        // Helper function to translate district code
        const translateDistrict = (districtCode: string | undefined): string => {
          if (!districtCode) return '';
          try {
            // Try to get translation from districts namespace, fallback to code if not found
            const translationKey = `districts.${districtCode}`;
            const translated = t(translationKey as any);
            // If translation returns the key itself (meaning not found), return the original code
            // Otherwise return the translated value
            return translated && !translated.startsWith('districts.') ? translated : districtCode;
          } catch {
            return districtCode;
          }
        };

        csvContent = [
          headers.join(','),
          ...reportItems.map((row: AppointmentSummaryReportItem) => [
            `"${row.appointmentDateTime || ''}"`,
            `"${row.status || ''}"`,
            `"${row.payment || ''}"`,
            row.extraMed ? 'Yes' : 'No',
            `"${row.deliveryWaybill || ''}"`,
            `"${row.timestamp || ''}"`,
            `"${row.lastnameEn || ''}"`,
            `"${row.firstnameEn || ''}"`,
            `"${row.chineseName || ''}"`,
            `"${row.gender || ''}"`,
            `"${row.dob || ''}"`,
            `"${row.hkid || ''}"`,
            `"${row.appUserPhone || ''}"`,
            `"${(row.deliveryAddress || '').replace(/"/g, '""')}"`, // Escape quotes in address
            `"${row.transactionId || ''}"`,
            `"${translateDistrict(row.district)}"`,
            row.transactionTotal !== undefined && row.transactionTotal !== null ? row.transactionTotal : '',
            `"${row.doctorName || ''}"`,
            `"${row.tenant || ''}"`,
            `"${row.payor || ''}"`,
          ].join(','))
        ].join('\n');
      }

      // Create and download file with BOM for Excel compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const dateRangeStr = startDateStr === endDateStr 
        ? startDateStr 
        : `${startDateStr}_to_${endDateStr}`;
      const filename = reportType === 'default' 
        ? `appointment-summary-${dateRangeStr}.csv`
        : `appointment-summary-${reportType}-${dateRangeStr}.csv`;
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      const dateRangeDisplay = startDateStr === endDateStr 
        ? startDateStr 
        : `${startDateStr} to ${endDateStr}`;
      message.success(`Report exported successfully! ${reportItems.length} appointments included for ${dateRangeDisplay}.`);
    } catch (error) {
      message.error('Failed to export report: ' + (error as Error).message);
    }
  };

  const handleExportConfirm = async () => {
    if (!selectedExportDateRange) {
      message.warning('Please select a date range');
      return;
    }

    setExportDateModalVisible(false);
    await exportReportByDateRange(
      selectedExportDateRange,
      selectedReportType,
      selectedTenant,
      selectedPayor,
    );
  };

  return (
    <>
      <Button
        icon={<DownloadOutlined />}
        onClick={showExportDateModal}
        loading={externalLoading || reportByDateRangeLoading}
        type="default"
      >
        {t('common.exportReport')}
      </Button>

      {/* Export Date Range Selection Modal */}
      <Modal
        title={t('common.selectExportDateRange') || 'Select Export Date Range'}
        open={exportDateModalVisible}
        onOk={handleExportConfirm}
        onCancel={() => setExportDateModalVisible(false)}
        okText={t('common.export')}
        cancelText={t('common.cancel')}
        confirmLoading={reportByDateRangeLoading}
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              {t('common.selectDateRangeForExport') || 'Please select a date range for export:'}
            </Text>
            <RangePicker
              value={selectedExportDateRange}
              onChange={(dates) => setSelectedExportDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
              style={{ width: '100%' }}
              placeholder={[t('appointments.startDate'), t('appointments.endDate')]}
              format="YYYY-MM-DD"
              disabledDate={(current) => current && current > dayjs().endOf('day')}
            />
            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button
                size="small"
                onClick={() => {
                  const today = dayjs();
                  setSelectedExportDateRange([today, today]);
                }}
              >
                {t('common.today') || 'Today'}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  const today = dayjs();
                  const last7Days = today.subtract(6, 'day');
                  setSelectedExportDateRange([last7Days, today]);
                }}
              >
                {t('common.last7Days') || 'Last 7 Days'}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  const today = dayjs();
                  const lastMonthStart = today.subtract(1, 'month').startOf('month');
                  const lastMonthEnd = today.subtract(1, 'month').endOf('month');
                  setSelectedExportDateRange([lastMonthStart, lastMonthEnd]);
                }}
              >
                {t('common.lastMonth') || 'Last Month'}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  const today = dayjs();
                  const thisMonthStart = today.startOf('month');
                  setSelectedExportDateRange([thisMonthStart, today]);
                }}
              >
                {t('common.thisMonth') || 'This Month'}
              </Button>
            </div>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              {t('common.selectReportType') || 'Report Type:'}
            </Text>
            <Select
              value={selectedReportType}
              onChange={setSelectedReportType}
              style={{ width: '100%' }}
            >
              <Option value="default">{t('common.default') || 'Default'}</Option>
              <Option value="waybill-export-sf">{t('common.waybillExportSf') || 'Waybill Export - SF'}</Option>
              <Option value="waybill-export-jd">{t('common.waybillExportJd') || 'Waybill Export - JD'}</Option>
            </Select>
          </div>
          {selectedReportType === 'default' && (
            <>
              <div style={{ marginTop: 16 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  Tenant:
                </Text>
                <Select
                  value={selectedTenant}
                  onChange={setSelectedTenant}
                  style={{ width: '100%' }}
                >
                  {TENANT_OPTIONS.map((option) => (
                    <Option key={option.value || 'all'} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </div>
              <div style={{ marginTop: 16 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  Payor:
                </Text>
                <Select
                  value={selectedPayor}
                  onChange={setSelectedPayor}
                  style={{ width: '100%' }}
                >
                  <Option value="">All</Option>
                  {(payorsData?.insurancePayors || []).map((payor: string) => (
                    <Option key={payor} value={payor}>
                      {payor}
                    </Option>
                  ))}
                </Select>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default AppointmentReportExport;

